import { BodyFormat, EndpointParam } from './apiIntegrations.types';

/*
 * An illustrative request body built from an endpoint's body params, so people
 * reviewing an integration can see what actually goes out. Nested shapes come
 * from the notation the parser writes into param descriptions:
 * "address {street, city, state.code}" or "list of insureds {name, coverages[].limit}".
 */

type Shape = { [key: string]: unknown };

const PLACEHOLDER = '…';

export function isBodyParam(p: EndpointParam): boolean {
  return p.location === 'body' || p.location === 'raw_body';
}

/** Index of the brace that closes the one at `open`, or -1. */
function closingBrace(text: string, open: number): number {
  let depth = 0;
  for (let i = open; i < text.length; i++) {
    if (text[i] === '{') depth++;
    else if (text[i] === '}' && --depth === 0) return i;
  }
  return -1;
}

/** Splits on `sep` outside braces. */
function splitOutsideBraces(text: string, sep: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '{') depth++;
    else if (text[i] === '}') depth--;
    else if (text[i] === sep && depth === 0) {
      parts.push(text.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(text.slice(start));
  return parts.map(p => p.trim()).filter(Boolean);
}

function addPath(target: Shape, path: string): void {
  const [head, ...restParts] = splitOutsideBraces(path, '.');
  if (!head) return;
  const rest = restParts.join('.');
  const brace = head.indexOf('{');
  if (brace === 0) {
    // A group: "a.{b, c}" means a.b and a.c.
    const close = closingBrace(head, 0);
    splitOutsideBraces(head.slice(1, close < 0 ? undefined : close), ',').forEach(f => addPath(target, f));
    return;
  }
  if (brace > 0) {
    // Shorthand: "address {street, city}" or "contacts[] {name}" is "address.{street, city}".
    addPath(target, [`${head.slice(0, brace).trim()}.${head.slice(brace)}`, ...restParts].join('.'));
    return;
  }
  // Notes after the name aren't part of it: "code (EACH_OCC|AGG|MED)" is the field
  // "code", and its first allowed value makes a better example than a placeholder.
  const name = head.split(/[\s(]/)[0];
  const isList = name.endsWith('[]');
  const key = isList ? name.slice(0, -2) : name;
  if (!key) return;
  if (!rest) {
    const allowed = /\(([^)]*\|[^)]*)\)/.exec(head)?.[1].split('|')[0].trim();
    if (!(key in target)) target[key] = isList ? [] : allowed || PLACEHOLDER;
    return;
  }
  if (isList) {
    const list = target[key];
    if (!Array.isArray(list) || typeof list[0] !== 'object' || list[0] === null) target[key] = [{}];
    addPath((target[key] as Shape[])[0], rest);
  } else {
    const child = target[key];
    if (typeof child !== 'object' || child === null || Array.isArray(child)) target[key] = {};
    addPath(target[key] as Shape, rest);
  }
}

/** The fields named in a description's first {...} group, as a nested object. */
export function shapeFromDescription(text?: string | null): Shape | null {
  const open = (text || '').indexOf('{');
  if (open < 0) return null;
  const close = closingBrace(text!, open);
  const shape: Shape = {};
  splitOutsideBraces(text!.slice(open + 1, close < 0 ? undefined : close), ',').forEach(f => addPath(shape, f));
  return Object.keys(shape).length ? shape : null;
}

function exampleValue(p: EndpointParam): unknown {
  if (p.enum_values?.length) return p.enum_values[0];
  switch (p.type) {
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return true;
    case 'object':
      return shapeFromDescription(p.description) ?? {};
    case 'array': {
      const shape = shapeFromDescription(p.description);
      return shape ? [shape] : [];
    }
    default:
      return PLACEHOLDER;
  }
}

/** The example body as text: JSON, or key=value pairs for a form body. */
export function exampleBodyText(params: EndpointParam[], format: BodyFormat): string {
  const whole = params.find(p => p.location === 'raw_body');
  if (whole) {
    const value = exampleValue(whole);
    return typeof value === 'string' ? `(the value of "${whole.name}", sent as-is)` : JSON.stringify(value, null, 2);
  }
  const body: Shape = {};
  params.forEach(p => { body[p.name] = exampleValue(p); });
  if (format === 'form') {
    return Object.entries(body).map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`).join('&');
  }
  return JSON.stringify(body, null, 2);
}
